import {h, Component, createRef, RefObject} from 'preact';
import {ui, core} from '@playkit-js/kaltura-player-js';
import {Button, ButtonType, ButtonSize} from '@playkit-js/common/dist/components/button';
import {OnClickEvent} from '@playkit-js/common/dist/hoc/a11y-wrapper';
import {AudioIcon} from '../audio-icon';
import {AadEventTriggers} from '../../types/events';

import * as styles from './aad-overlay.scss';

const {Space} = ui.utils.KeyCode;

// TODO use values from playkit-ui
const KeyR = 'KeyR';
const KeyS = 'KeyS';
const KeyK = 'KeyK';

const {
  components: {PLAYER_SIZE},
  redux: {connect},
  reducers: {shell}
} = KalturaPlayer.ui;
const {Text, withText} = KalturaPlayer.ui.preacti18n;

const HIDE_BOTTOM_BAR_CLASS = 'bottom-bar-hidden';

export interface InfoProps {
  captionContent: string;
  onReplay: (trigger: AadEventTriggers) => void;
  onSkip: (trigger: AadEventTriggers) => void;
  onPauseResume: (trigger: AadEventTriggers) => boolean;
  onScroll: () => void;
  skipButtonLabel?: string;
  replayButtonLabel?: string;
  pauseButtonLabel?: string;
  resumeButtonLabel?: string;
  hideButtonLabel?: string;
  fullScreenOverlay?: boolean;
  hideBottomBar?: () => void;
  showBottomBar?: () => void;
}
interface ConnectProps {
  playerSize?: string;
}

interface AadOverlayState {
  contentVisible: boolean;
  paused: boolean;
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    hideBottomBar: () => dispatch(shell.actions.addPlayerClass(HIDE_BOTTOM_BAR_CLASS)),
    showBottomBar: () => dispatch(shell.actions.removePlayerClass(HIDE_BOTTOM_BAR_CLASS))
  };
};

type MergedProps = InfoProps & ConnectProps;

const mapStateToProps = (state: Record<string, any>) => ({
  playerSize: state.shell.playerSize
});

const translates = ({}: InfoProps) => {
  return {
    skipButtonLabel: <Text id="plugin-advanced-audio-description.skip">Skip</Text>,
    replayButtonLabel: <Text id="plugin-advanced-audio-description.replay">Replay</Text>,
    pauseButtonLabel: <Text id="plugin-advanced-audio-description.pause">Pause</Text>,
    resumeButtonLabel: <Text id="plugin-advanced-audio-description.resume">Play</Text>,
    hideButtonLabel: <Text id="plugin-advanced-audio-description.hide">Hide description text</Text>
  };
};

class AadOverlay extends Component<MergedProps, AadOverlayState> {
  private _pauseResumeButtonRef: RefObject<HTMLButtonElement> = createRef();
  private _overlayRef: RefObject<HTMLDivElement> = createRef();

  constructor(props: MergedProps) {
    super(props);
    this.state = {
      contentVisible: true,
      paused: false
    };
  }

  componentDidMount(): void {
    this.props.hideBottomBar!();
    document.addEventListener('keydown', this.handleKeyDown);
  }

  getFocusableElements = (): HTMLElement[] => {
    if (!this._overlayRef.current) {
      return [];
    }
    
    // Query for all focusable elements within the overlay
    const focusableSelector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const elements = this._overlayRef.current.querySelectorAll<HTMLElement>(focusableSelector);
    
    return Array.from(elements).filter(el => !el.closest('[hidden]') && el.offsetWidth > 0);
  };

  handleFocusTrap = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) {
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab: moving backwards
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: moving forwards
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  };

  componentWillUnmount(): void {
    this.props.showBottomBar!();
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (event: KeyboardEvent) => {
    const {onReplay, onSkip} = this.props;
    
    if (this._overlayRef.current) {
      this.handleFocusTrap(event);
    }
    
    if (event.code === Space || event.code === KeyK) {
      event.preventDefault();
      this.handlePauseResume(event, true, AadEventTriggers.SHORTCUT);
    }

    if (event.code == KeyS && event.shiftKey) {
      event.preventDefault();
      onSkip(AadEventTriggers.SHORTCUT);
    }

    if (event.code == KeyR && event.shiftKey) {
      event.preventDefault();
      onReplay(AadEventTriggers.SHORTCUT);
    }
  };

  handleHideContent = (e: OnClickEvent, byKeyboard: boolean) => {
    this.setState({contentVisible: false});
    if (byKeyboard) {
      this._pauseResumeButtonRef.current?.focus();
    }
  };

  handlePauseResume = (e: OnClickEvent, byKeyboard: boolean, trigger: AadEventTriggers) => {
    if (e.shiftKey && byKeyboard) {
      // Ignore Shift + Space
      this.props.onSkip(AadEventTriggers.SHORTCUT);
      return;
    }
    const {onPauseResume} = this.props;
    const paused = onPauseResume(trigger);
    this.setState({paused});
  };

  renderContentWrapper = () => {
    const {captionContent, onScroll} = this.props;
    if (!this.state.contentVisible) {
      return null;
    }
    return (
      <div className={styles.aadContentWrapper} data-testid="aadContentWrapper">
        {this.renderCloseButton()}
        <div className={styles.aadPlaybackIcon} data-testid="aadPlaybackIcon" aria-hidden="true">
          <AudioIcon isActive={!this.state.paused} />
        </div>
        <div className={styles.captionContentWrapper} data-testid="aadCaptionContent" onScroll={onScroll} tabIndex={0}>
          {captionContent}
        </div>
      </div>
    );
  };

  renderButtonsWrapper = () => {
    const {skipButtonLabel, replayButtonLabel, pauseButtonLabel, resumeButtonLabel, onReplay, onSkip} = this.props;
    return (
      <div className={styles.aadButtonsWrapper} data-testid="aadButtonsWrapper">
        <div className={styles.buttonWrapper}>
          <Button
            focusOnMount
            type={ButtonType.translucent}
            size={ButtonSize.large}
            className={styles.pauseResumeButton}
            onClick={(e, byKeyboard) => this.handlePauseResume(e, byKeyboard, AadEventTriggers.BUTTON)}
            testId="aadPauseResumeButton"
            setRef={ref => (this._pauseResumeButtonRef.current = ref)}
          >
            {this.state.paused ? resumeButtonLabel : pauseButtonLabel}
          </Button>
          <div className={styles.buttonHint}>K</div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button type={ButtonType.translucent} size={ButtonSize.large} onClick={() => onSkip(AadEventTriggers.BUTTON)} testId="aadSkipButton">
            {skipButtonLabel}
          </Button>
          <div className={styles.buttonHint}>Shift+S</div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button type={ButtonType.translucent} size={ButtonSize.large} onClick={() => onReplay(AadEventTriggers.BUTTON)} testId="aadReplayButton">
            {replayButtonLabel}
          </Button>
          <div className={styles.buttonHint}>Shift+R</div>
        </div>
      </div>
    );
  };

  renderCloseButton = () => {
    const {fullScreenOverlay, hideButtonLabel} = this.props;
    if (fullScreenOverlay) {
      return null;
    }
    return (
      <Button
        icon={'close'}
        type={ButtonType.borderlessTranslucent}
        onClick={this.handleHideContent}
        className={styles.closeButton}
        testId="aadHideButton"
        ariaLabel={hideButtonLabel}
      />
    );
  };

  render() {
    const {playerSize = '', fullScreenOverlay} = this.props;
    if (playerSize === PLAYER_SIZE.TINY) {
      return null;
    }
    const aadRootClass = [styles.aadRoot, styles[playerSize]];
    if (fullScreenOverlay) {
      aadRootClass.push(styles.fullScreenOverlay);
    }
    return (
      <div 
        className={aadRootClass.join(' ')} 
        data-testid={`aadRoot${fullScreenOverlay ? '-fullScreen' : ''}`}
        ref={this._overlayRef}
        role="dialog"
        aria-modal="true"
      >
        {this.renderContentWrapper()}
        {this.renderButtonsWrapper()}
      </div>
    );
  }
}

export default withText(translates)(connect(mapStateToProps, mapDispatchToProps)(AadOverlay));
